// sales-leads-v1
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Banknote, Building2, CalendarClock, FileText, Loader2, Mail, MapPin, MessageCircle, NotebookPen, Pencil, Phone, Trash2, Trophy, User, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RequirementsCard } from "@/components/sales/RequirementsCard";
import { DealFlow } from "@/components/sales/DealFlow";
import { LeadDialog } from "@/components/sales/LeadDialog";
import { ACTIVITY_KINDS, STAGES, agoText, budget, dmyHm, followup, inDays, splitIso, stageLabel, toIso } from "@/components/sales/leadUtils";

import { confirmAction } from "@/lib/dialogs";
const SF = "/erp/delivery/sales-flow";
const CARD = "rounded-2xl border border-border bg-card";
const digits = (p) => (p || "").replace(/\D/g, "");
const KIND_ICON = { CALL: Phone, WHATSAPP: MessageCircle, EMAIL: Mail, MEETING: User, SITE_VISIT: MapPin, NOTE: NotebookPen, SYSTEM: FileText };

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 border-b border-border py-3 last:border-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0"><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-0.5 break-words text-sm text-foreground">{children || "—"}</div></div>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [acts, setActs] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [owners, setOwners] = useState([]);
  const [clients, setClients] = useState([]);
  const [missing, setMissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostWhy, setLostWhy] = useState("");
  const [kind, setKind] = useState("CALL");
  const [text, setText] = useState("");
  const [fuD, setFuD] = useState(""); const [fuT, setFuT] = useState("09:00");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, a, p] = await Promise.all([api.get(`/pipeline/leads/${id}`), api.get(`/pipeline/leads/${id}/activities`), api.get("/pipeline/proposals").catch(() => ({ data: [] }))]);
      setLead(l.data); setActs(a.data); setProposals((p.data || []).filter((x) => x.lead_id === id));
    } catch (ex) { if (ex?.response?.status === 404) setMissing(true); else toast.error(getErrorMessage(ex)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/pipeline/owners").then(({ data }) => setOwners(data)).catch(() => {});
    api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const patch = async (body, msg) => {
    try { const { data } = await api.patch(`/pipeline/leads/${id}`, body); setLead(data); if (msg) toast.success(msg); const a = await api.get(`/pipeline/leads/${id}/activities`); setActs(a.data); return true; }
    catch (ex) { toast.error(getErrorMessage(ex), { duration: 8000 }); return false; }
  };
  const setStage = async (s) => { if (s !== lead.stage) await patch({ stage: s }, `Moved to ${stageLabel(s)}`); };
  const won = () => { document.querySelector("[data-testid=deal-flow]")?.scrollIntoView({ behavior: "smooth" }); toast.info("Follow Deal progress: send the proposal, get the design approved, then book the PO.", { duration: 7000 }); };
  const markLost = async () => { if (await patch({ stage: "LOST", lost_reason: lostWhy.trim() || null }, "Marked as lost")) setLostOpen(false); };
  const remove = async () => {
    if (!await confirmAction(`Delete ${lead.code} - ${lead.title}? Its timeline is deleted too.`)) return;
    try { await api.delete(`/pipeline/leads/${id}`); toast.success("Lead deleted"); navigate(SF); } catch (ex) { toast.error(getErrorMessage(ex), { duration: 8000 }); }
  };
  const log = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/pipeline/leads/${id}/activities`, { kind, body: text.trim(), next_followup_at: toIso(fuD, fuT) }); setText(""); setFuD(""); toast.success("Activity logged"); load(); }
    catch (ex) { toast.error(getErrorMessage(ex), { duration: 8000 }); } finally { setBusy(false); }
  };
  if (missing) return <div className="space-y-3 py-10 text-center"><p className="text-muted-foreground">This lead was not found.</p><Button asChild variant="outline"><Link to={SF}>Back to Sales Flow</Link></Button></div>;
  if (!lead) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const closedLead = ["WON", "LOST"].includes(lead.stage);
  const fu = followup(lead.next_followup_at); const fuParts = splitIso(lead.next_followup_at);
  const phone = digits(lead.contact_phone);
  const setFu = (date, time) => patch({ next_followup_at: toIso(date, time || fuParts.time || "09:00") });

  return (
    <div className="space-y-5" data-testid="lead-detail">
      <Link to={SF} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Sales Flow</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={lead.stage === "WON" ? "default" : lead.stage === "LOST" ? "destructive" : "secondary"} data-testid="lead-stage-badge">{stageLabel(lead.stage)}</Badge>
            <Badge variant="outline">{lead.priority[0] + lead.priority.slice(1).toLowerCase()} priority</Badge>
            {lead.source && <Badge variant="outline">{lead.source}</Badge>}
            <span className="font-mono text-xs text-muted-foreground">{lead.code}</span>
          </div>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{lead.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" asChild disabled={!phone}><a href={phone ? `tel:${lead.contact_phone}` : undefined}><Phone className="h-4 w-4" /> Call</a></Button>
          <Button variant="outline" size="sm" asChild disabled={!phone}><a href={phone ? `https://wa.me/${phone.length === 10 ? `91${phone}` : phone}` : undefined} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a></Button>
          <Button variant="outline" size="sm" asChild disabled={!lead.contact_email}><a href={lead.contact_email ? `mailto:${lead.contact_email}` : undefined}><Mail className="h-4 w-4" /> Email</a></Button>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="lead-edit"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button variant="outline" size="icon" className="h-8 w-8 text-red-600" onClick={remove} title="Delete lead" data-testid="lead-delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className={`${CARD} flex flex-wrap items-center gap-1.5 p-2`} data-testid="lead-stage-tabs">
        {STAGES.map((s) => (
          <button key={s.key} type="button" disabled={closedLead} onClick={() => setStage(s.key)} data-testid={`stage-${s.key}`} aria-pressed={lead.stage === s.key || (lead.stage === "TENDER" && s.key === "QUALIFIED")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition disabled:opacity-50 ${lead.stage === s.key ? "bg-[#910029] text-white" : "text-muted-foreground hover:bg-secondary"}`}>{s.label}</button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <button type="button" onClick={won} data-testid="stage-WON" className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${lead.stage === "WON" ? "bg-emerald-600 text-white" : "text-emerald-700 hover:bg-emerald-50"}`}><Trophy className="h-3.5 w-3.5" /> Won</button>
        <button type="button" disabled={lead.stage === "LOST"} onClick={() => { setLostWhy(lead.lost_reason || ""); setLostOpen(true); }} data-testid="stage-LOST" className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${lead.stage === "LOST" ? "bg-red-600 text-white" : "text-red-600 hover:bg-red-50"}`}><XCircle className="h-3.5 w-3.5" /> Lost</button>
      </div>

      <DealFlow lead={lead} proposals={proposals} onChanged={load} />

      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <div className="space-y-5">
          <div className={`${CARD} px-4 py-1`}>
            <Row icon={Phone} label="Phone">{lead.contact_phone}</Row>
            <Row icon={Mail} label="Email">{lead.contact_email}</Row>
            <Row icon={MapPin} label="Location">{lead.location}</Row>
            <Row icon={Building2} label="Project type">{lead.project_type}</Row>
            <Row icon={Banknote} label="Budget">{lead.estimated_value ? budget(lead.estimated_value) : ""}</Row>
            <Row icon={Building2} label="Client record">{(() => { const c = clients.find((x) => x.id === lead.client_id); return c ? <Link to="/erp/delivery/clients" className="font-medium text-[#910029] hover:underline" data-testid="lead-client-link">{c.code} · {c.name}</Link> : lead.client_id ? "Linked" : "Added to Clients when the lead is qualified"; })()}</Row>
            <Row icon={User} label="Owner">{lead.owner_name}</Row>
            <Row icon={FileText} label="Requirement">{lead.requirement}</Row>
            {lead.stage === "LOST" && <Row icon={XCircle} label="Lost because">{lead.lost_reason}</Row>}
          </div>

          <RequirementsCard lead={lead} onSaved={setLead} />

          <div className={`${CARD} p-4`} data-testid="lead-followup">
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Next follow-up</span>
              {lead.next_followup_at && <button type="button" onClick={() => patch({ next_followup_at: null })} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}</div>
            <div className="mt-1 text-lg font-semibold">{lead.next_followup_at ? dmyHm(lead.next_followup_at) : "Not set"} {fu && <span className={`text-xs font-medium ${fu.tone === "overdue" ? "text-red-600" : fu.tone === "today" ? "text-amber-600" : "text-muted-foreground"}`}>({fu.text})</span>}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[["Tomorrow", 1], ["In 3 days", 3], ["Next week", 7]].map(([t, n]) => <button key={t} type="button" onClick={() => setFu(inDays(n))} className="rounded-full border border-border bg-secondary px-3 py-1 text-[11px] font-semibold uppercase tracking-wide hover:bg-accent">{t}</button>)}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Input type="date" value={fuParts.date} onChange={(e) => e.target.value && setFu(e.target.value, fuParts.time)} aria-label="Follow-up date" />
              <Input type="time" value={fuParts.time} onChange={(e) => fuParts.date && e.target.value && setFu(fuParts.date, e.target.value)} aria-label="Follow-up time" />
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className={`${CARD} p-4`} data-testid="lead-log">
            <div className="flex flex-wrap gap-1.5">
              {ACTIVITY_KINDS.map((k) => { const I = KIND_ICON[k.key]; return (
                <button key={k.key} type="button" onClick={() => setKind(k.key)} data-testid={`kind-${k.key}`} aria-pressed={kind === k.key}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${kind === k.key ? "border-[#910029] bg-[#910029] text-white" : "border-border bg-background text-foreground hover:bg-secondary"}`}><I className="h-3.5 w-3.5" />{k.label}</button>
              ); })}
            </div>
            <Textarea rows={3} className="mt-3" placeholder="What did you discuss?" value={text} onChange={(e) => setText(e.target.value)} data-testid="log-text" />
            <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
              <div><Label className="text-xs text-muted-foreground">Next follow-up (optional)</Label>
                <div className="mt-1 flex gap-2"><Input type="date" value={fuD} onChange={(e) => setFuD(e.target.value)} className="w-40" /><Input type="time" value={fuT} onChange={(e) => setFuT(e.target.value)} className="w-32" /></div></div>
              <Button onClick={log} disabled={busy || !text.trim()} data-testid="log-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Log activity</Button>
            </div>
          </div>

          <div className={`${CARD} p-4`} data-testid="lead-timeline">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Timeline</div>
            <ul className="mt-3 space-y-3">
              {acts.map((a) => { const I = KIND_ICON[a.kind] || FileText; return (
                <li key={a.id} className="flex gap-3" data-testid="timeline-item">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground"><I className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0"><div className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{a.kind === "SYSTEM" ? "System" : ACTIVITY_KINDS.find((k) => k.key === a.kind)?.label}</span> · {dmyHm(a.created_at)}{a.by_name ? ` · ${a.by_name}` : ""} · {agoText(a.created_at)}</div>
                    <p className="mt-0.5 whitespace-pre-line break-words text-sm text-foreground">{a.body}</p></div>
                </li>
              ); })}
              {acts.length === 0 && <li className="text-sm text-muted-foreground">Nothing logged yet.</li>}
            </ul>
          </div>
        </div>
      </div>

      <LeadDialog open={editing} onOpenChange={setEditing} lead={lead} owners={owners} clients={clients} onSaved={load} />
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-dialog">
          <DialogHeader><DialogTitle>Mark as lost</DialogTitle></DialogHeader>
          <Label>Why was it lost?</Label>
          <Input value={lostWhy} onChange={(e) => setLostWhy(e.target.value)} placeholder="e.g. went with another builder, budget too low" data-testid="lost-reason" />
          <DialogFooter><Button variant="destructive" onClick={markLost} data-testid="lost-confirm">Mark as lost</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
